import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, Renderer2, ViewChild, computed, effect, inject, input, output, signal, DOCUMENT } from '@angular/core';
import { CarouselComponent, CarouselModule, OwlOptions } from 'ngx-owl-carousel-o';
import { CardLoaderComponent } from 'src/app/components/card-loader/card-loader.component';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import { roundOff } from 'src/app/core/utilities/number-formater.utility';
import { CUSTOM_OPTIONS } from 'src/app/home/components/dynamic-template/dynamic-template.component';
import { CategoryInfo } from 'src/app/home/models/home.model';
import { CategoryManagementService } from 'src/app/home/services/category-management.service';
import { ProductListing } from 'src/app/products/models/product-listing.model';
import { PriceService } from 'src/app/products/services/price.service';
import { ProductsRepositoryService } from 'src/app/products/services/products-repository.service';
import { SafeHtmlPipe } from 'src/app/shared/pipes/safe-html.pipe';
import { DynamicHomeProductCardComponent } from '../../dynamic-home-product-card/dynamic-home-product-card.component';

export interface SectionDetails {
  id: string;
  name: string;
  image: string;
  url_path: string;
}

@Component({
  selector: 'webstore-side-banner-product-list',
  standalone: true,
  imports: [
    CommonModule,
    CarouselModule,
    SafeHtmlPipe,
    CardLoaderComponent,
    DynamicHomeProductCardComponent
  ],
  templateUrl: './side-banner-product-list.component.html',
  styleUrls: ['./side-banner-product-list.component.scss']
})
export class SideBannerProductListComponent implements AfterViewInit, OnDestroy {
  private productsRepositoryService = inject(ProductsRepositoryService);
  private storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);
  private categoryManagementService = inject(CategoryManagementService);
  private elementRef = inject(ElementRef);
  private document = inject<Document>(DOCUMENT);
  private renderer = inject(Renderer2);
  private cdr = inject(ChangeDetectorRef);
  private priceService = inject(PriceService);

  @ViewChild('carouselListDiv') section!: ElementRef;
  @ViewChild('sideBannerCarouselList') carouselList!: CarouselComponent;

  readonly customOptions = input<OwlOptions>(CUSTOM_OPTIONS);
  readonly carouselListNavigationSection = input<string>('');
  readonly baseHtml = input<string>('');
  readonly sectionId = input<string>('');
  readonly countDownTemplate = input<boolean>(false);
  readonly showCashpoint = input<boolean>(false);
  readonly showPercentageDiscount = input<boolean>(true);
  readonly showAddToCart = input<boolean>(false);
  readonly isMobile = input<boolean>(false);
  readonly sectionConfiguration = input<any>(null);

  readonly collectionIdsInput = input<string[]>([]);
  readonly categoriesInfoInput = input<CategoryInfo[]>([]);

  readonly addToCartClicked$ = output<boolean>();
  readonly productClicked$ = output<string>();
  readonly goToCategoryPage$ = output<string>();
  readonly goToCollectionPage$ = output<string>();

  readonly loader = signal<boolean>(false);
  readonly selectedIndex = signal<number>(0);
  readonly countDownPresent = signal<boolean>(false);
  readonly sectionDetails = signal<SectionDetails[]>([]);
  readonly productListByIndex = signal<{ [key: number]: any[] }>({});
  readonly productFetchState = signal<{ [key: number]: boolean }>({});

  // Computed signals
  readonly showOutOfStockProducts = computed(() =>
    this.storeConfigurationRepositoryService.showOutOfStockProducts()
  );

  readonly currentProducts = computed(() => {
    const index = this.selectedIndex();
    return this.productListByIndex()[index] || [];
  });

  constructor() {
    effect(() => {
      const collectionIds = this.collectionIdsInput();
      if (collectionIds?.length) {
        this.loader.set(true);
        this.configureCollectionListData(collectionIds);
      }
    });

    effect(() => {
      const categoriesInfo = this.categoriesInfoInput();
      const isCategoryServiceReady = this.categoryManagementService.isReady();
      if (categoriesInfo?.length && isCategoryServiceReady) {
        this.loader.set(true);
        this.configureCategoryListData(categoriesInfo);
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.section?.nativeElement) {
      const sectionIdValue = this.sectionId();
      this.section.nativeElement.id = sectionIdValue;
      const sectionToAddClass = this.document.getElementById(sectionIdValue);
      if (sectionToAddClass) {
        sectionToAddClass.classList.add(`style-${sectionIdValue}`);
      }
    }
  }

  async configureCollectionListData(collectionIds: string[]): Promise<void> {
    this.loader.set(true);

    try {
      // Fetch collections sequentially to avoid signal race condition
      const validCollections: SectionDetails[] = [];

      for (const collectionId of collectionIds) {
        try {
          await this.productsRepositoryService.getCollectionDetails(collectionId);
          const collection = this.productsRepositoryService.collection();

          if (collection) {
            validCollections.push({
              id: collection.id,
              name: collection.name,
              image: collection.image || '',
              url_path: collection.url_path || ''
            });
          }
        } catch {
          // Silently skip failed collections
        }
      }

      this.sectionDetails.set(validCollections);

      // Fetch products for first tab only
      await this.fetchProductsForIndex(0);
      this.addClickActionsToCarouselList();
    } finally {
      this.loader.set(false);
    }
  }

  async fetchProductsForIndex(index: number): Promise<void> {
    // Check if already fetched
    const fetchState = this.productFetchState();
    if (fetchState[index]) {
      return; // Already cached
    }

    const sectionDetail = this.sectionDetails()[index];
    if (!sectionDetail) return;

    this.loader.set(true);

    try {
      const collectionIds = this.collectionIdsInput();

      if (collectionIds?.length) {
        // Fetch products for collection
        const params = {
          currentFilters: {
            collection_ids: sectionDetail.id,
            ...(this.showOutOfStockProducts() ? {} : { in_stock: true })
          },
          pageNumber: 0,
          pageSize: 1000,
          search: '',
          sortActive: '',
          sortDirection: '',
          ordering: '-created_at'
        };

        await this.productsRepositoryService.getProducts(params);
        const productsData = this.productsRepositoryService.products();

        if (productsData) {
          const products = productsData.hits?.hits?.map((hit: any) => hit._source).map((product: any) => {
            product.mrp = typeof (product?.mrp) === 'string' ? product?.mrp : roundOff(product?.mrp ?? 0) as number;
            product.selling_price = roundOff(product?.price_list?.recommended_offers?.applicable_amount ?? 0) as number;
            return product;
          });

          this.countDownPresent.set(products?.some((product: any) => !!product?.deal) || false);

          // Update products for this index
          const currentProducts = this.productListByIndex();
          this.productListByIndex.set({ ...currentProducts, [index]: products });

          // Mark as fetched
          const currentFetchState = this.productFetchState();
          this.productFetchState.set({ ...currentFetchState, [index]: true });
        }
      }
    } catch (error) {
    } finally {
      this.loader.set(false);
    }
  }

  async configureCategoryListData(categoriesInfo: CategoryInfo[]): Promise<void> {
    this.loader.set(true);

    try {
      // Get category details from the CategoryManagementService
      const validCategories = categoriesInfo.map((categoryInfo) => {
        const categoryData = this.categoryManagementService.getCategoryById(categoryInfo?.id);

        if (categoryData?.url_path) {
          const { id = "", url_path = "", category_images = [], name = "", display_name = "" } = categoryData || {};
          const imageUrl = Array.isArray(category_images) && category_images.length > 0
            ? (typeof category_images[0] === 'string' ? category_images[0] : (category_images[0] as any)?.url || '')
            : '';

          return {
            id,
            url_path,
            name: display_name || name,
            image: imageUrl
          };
        }
        return null;
      }).filter((item): item is SectionDetails => item !== null);

      if (validCategories.length === 0) {
        this.loader.set(false);
        return;
      }

      this.sectionDetails.set(validCategories);

      // Fetch products for first tab only
      await this.fetchCategoryProductsForIndex(0);
      this.addClickActionsToCarouselList();
    } finally {
      this.loader.set(false);
    }
  }

  async fetchCategoryProductsForIndex(index: number): Promise<void> {
    // Check if already fetched
    const fetchState = this.productFetchState();
    if (fetchState[index]) {
      return; // Already cached
    }

    const sectionDetail = this.sectionDetails()[index];
    if (!sectionDetail) return;

    this.loader.set(true);

    try {
      const categoriesInfo = this.categoriesInfoInput();

      if (categoriesInfo?.length) {
        // Fetch products for category
        const params = {
          currentFilters: {
            category_url_paths: sectionDetail.url_path,
            ...(this.showOutOfStockProducts() ? {} : { in_stock: true })
          },
          pageNumber: 0,
          pageSize: 20,
          search: '',
          sortActive: '',
          sortDirection: '',
          ordering: '-created_at'
        };

        await this.productsRepositoryService.getProductsV2(params);
        const productsData = this.productsRepositoryService.productsV2();

        if (productsData?.hits?.hits?.length) {
          const products = productsData.hits.hits.map((hit: any) => {
            let productListing = hit._source as ProductListing;

            if (productListing?.variants && productListing.variants.length > 0) {
              const productVariant = productListing.variants?.filter(product => product.is_active && product.is_visible)[0];
              if (productVariant) {
                const { actualPrice, sellingPrice } = this.priceService?.getProductListingPrice(productVariant);
                productVariant.mrp = actualPrice;
                productVariant.selling_price = sellingPrice;
                return { ...productVariant };
              }
            }
            return null;
          }).filter(product => product !== null);

          this.countDownPresent.set(products?.some((product: any) => !!product?.deal) || false);

          // Update products for this index
          const currentProducts = this.productListByIndex();
          this.productListByIndex.set({ ...currentProducts, [index]: products });

          // Mark as fetched
          const currentFetchState = this.productFetchState();
          this.productFetchState.set({ ...currentFetchState, [index]: true });
        }
      }
    } catch (error) {
    } finally {
      this.loader.set(false);
    }
  }

  async changeSelectedIndex(index: number): Promise<void> {
    const currentIndex = this.selectedIndex();
    if (index === currentIndex) return;

    this.selectedIndex.set(index);

    // Check if products already fetched
    const fetchState = this.productFetchState();
    if (!fetchState[index]) {
      // Lazy load products for this tab
      const collectionIds = this.collectionIdsInput();
      const categoriesInfo = this.categoriesInfoInput();

      if (collectionIds?.length) {
        await this.fetchProductsForIndex(index);
      } else if (categoriesInfo?.length) {
        await this.fetchCategoryProductsForIndex(index);
      }
    }
  }

  addClickActionsToCarouselList(): void {
    // Use event delegation to handle dynamically added navigation buttons
    this.setupEventDelegation();

    // Also try direct attachment with retry for better compatibility
    this.setupDirectClickListeners();
  }

  private setupEventDelegation(): void {
    // Event delegation - listens on parent element for clicks on navigation buttons
    this.renderer.listen(this.elementRef.nativeElement, 'click', (event: Event) => {
      const target = event.target as HTMLElement;

      // Check if the clicked element or its parent is a navigation button
      const navButton = target.closest('.carousel-list-nav-prev, .carousel-list-nav-next');

      if (navButton && this.carouselList) {
        event.preventDefault();
        event.stopPropagation();

        if (navButton.classList.contains('carousel-list-nav-prev')) {
          this.carouselList.prev();
        } else if (navButton.classList.contains('carousel-list-nav-next')) {
          this.carouselList.next();
        }
      }
    });
  }

  private setupDirectClickListeners(): void {
    const attachListeners = () => {
      const previousSlideElement = this.elementRef.nativeElement.querySelector('.carousel-list-nav-prev');
      const nextSlideElement = this.elementRef.nativeElement.querySelector('.carousel-list-nav-next');

      if (previousSlideElement && this.carouselList) {
        this.renderer.listen(previousSlideElement, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.carouselList.prev();
        });
      }

      if (nextSlideElement && this.carouselList) {
        this.renderer.listen(nextSlideElement, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.carouselList.next();
        });
      }

      return !!(previousSlideElement && nextSlideElement);
    };

    // Try immediate attachment
    const attached = attachListeners();

    // If buttons not found, retry after a delay (for innerHTML rendered content)
    if (!attached) {
      setTimeout(() => attachListeners(), 100);
      setTimeout(() => attachListeners(), 300);
    }
  }

  navigateToCorrespondingPage(sectionDetail: SectionDetails): void {
    const collectionIds = this.collectionIdsInput();
    const categoriesInfo = this.categoriesInfoInput();

    if (collectionIds?.length && sectionDetail?.id) {
      this.goToCollectionPage$.emit(sectionDetail?.id);
    } else if (categoriesInfo?.length && sectionDetail?.url_path) {
      this.goToCategoryPage$.emit(sectionDetail?.url_path);
    }
  }

  ngOnDestroy(): void {
    // Cleanup
  }
}
